import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ConfirmationDialogueComponent } from './components/application/common/confirmation-dialogue/confirmation-dialogue.component';
import { ConfirmationDialogueWithFieldComponent } from './components/application/common/confirmation-dialogue-with-field/confirmation-dialogue-with-field.component';
import { ConfirmDialogComponent } from './shared/components/confirm-dialog/confirm-dialog.component';
import { PreloaderService } from '@core';
import { SharedService } from './services/core/shared.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements AfterViewInit, OnDestroy {
  private readonly pendingExitPrompts = new WeakSet<MatDialogRef<unknown>>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly preloader: PreloaderService,
    private readonly dialog: MatDialog,
    private readonly sharedService: SharedService
  ) {
    this.dialog.afterOpened.subscribe((dialogRef) => {
      this.applyDialogTheme(dialogRef);

      if (this.isConfirmationDialog(dialogRef)) {
        return;
      }

      dialogRef.backdropClick()
        .pipe(takeUntil(dialogRef.afterClosed()))
        .subscribe(() => this.promptBeforeDialogClose(dialogRef));
    });

    this.sharedService.themeChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.dialog.openDialogs.forEach((dialogRef) => this.applyDialogTheme(dialogRef));
      });
  }

  ngAfterViewInit() {
    this.preloader.hide();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isConfirmationDialog(dialogRef: MatDialogRef<unknown>): boolean {
    const instance = dialogRef.componentInstance;
    return instance instanceof ConfirmationDialogueComponent
      || instance instanceof ConfirmationDialogueWithFieldComponent
      || instance instanceof ConfirmDialogComponent;
  }

  private promptBeforeDialogClose(dialogRef: MatDialogRef<unknown>): void {
    if (this.pendingExitPrompts.has(dialogRef)) {
      return;
    }

    this.pendingExitPrompts.add(dialogRef);

    const confirmRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        title: 'Are You Sure?',
        message: 'You are about to exit this dialog.\n <strong>Any changes you have made will not be saved.</strong> \n\n Are you sure you want to exit?'
      },
    });

    confirmRef.afterClosed().pipe(take(1)).subscribe((confirmed) => {
      this.pendingExitPrompts.delete(dialogRef);

      if (confirmed) {
        dialogRef.close();
      }
    });
  }

  private applyDialogTheme(dialogRef: MatDialogRef<unknown>): void {
    const isDarkTheme = this.sharedService.darkMode || this.sharedService.userTheme === 'dark';
    dialogRef.removePanelClass('theme-dark');

    if (isDarkTheme) {
      dialogRef.addPanelClass('theme-dark');
    }
  }
}
